import React, { useCallback, useRef, useState } from 'react';
import { useKeydown } from '../../hooks/useKeydown';
import { InoPlayerProgressProps } from './InoPlayerProgress.types';

export const InoPlayerProgress: React.FC<InoPlayerProgressProps> = ({
  value = 0,
  buffered = 0,
  isActive = false,
  isLive = false,
  onChange,
  onDragStart,
  onDragEnd,
  onOk,
  onUp,
  onDown,
  classNames = '',
  showTooltip = true,
  duration = 0,
  showTime = true,
  showLastTime = false,
  showDuration = true,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(0);
  const [tooltipTime, setTooltipTime] = useState(0);
  const progressRef = useRef<HTMLDivElement>(null);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds
        .toString()
        .padStart(2, '0')}`;
    }

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleProgressChange = useCallback(
    (clientX: number) => {
      if (!progressRef.current) return;

      const rect = progressRef.current.getBoundingClientRect();
      const position = (clientX - rect.left) / rect.width;
      const clampedPosition = Math.max(0, Math.min(1, position));

      setTooltipPosition(clampedPosition * 100);
      setTooltipTime(duration * clampedPosition);
      onChange?.(clampedPosition * 100);
    },
    [duration, onChange]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        handleProgressChange(e.clientX);
      } else if (progressRef.current) {
        const rect = progressRef.current.getBoundingClientRect();
        if (e.clientY >= rect.top - 20 && e.clientY <= rect.bottom + 20) {
          const position = (e.clientX - rect.left) / rect.width;
          const clampedPosition = Math.max(0, Math.min(1, position));
          setTooltipPosition(clampedPosition * 100);
          setTooltipTime(duration * clampedPosition);
        } else {
          setTooltipPosition(-1);
        }
      }
    },
    [isDragging, duration, handleProgressChange]
  );

  const handleMouseDown = (e: React.MouseEvent | MouseEvent) => {
    if (isLive) return;
    setIsDragging(true);
    onDragStart?.();
    handleProgressChange(e.clientX);
  };

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      onDragEnd?.();
    }
  }, [isDragging, onDragEnd]);

  const handleClick = (e: React.MouseEvent) => {
    if (isLive) return;
    handleProgressChange(e.clientX);
  };

  const handleKeyboardNavigation = (direction: 'left' | 'right') => {
    if (!isActive) return;

    const step = 1; // 1% step for keyboard navigation
    const newValue =
      direction === 'left'
        ? Math.max(0, value - step)
        : Math.min(100, value + step);

    onChange?.(newValue);
  };

  useKeydown({
    isActive,
    left: () => handleKeyboardNavigation('left'),
    right: () => handleKeyboardNavigation('right'),
    up: onUp,
    down: onDown,
    ok: onOk,
    debounce: 50, // Smaller debounce for smoother control
  });

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      className={`
        ino-player-progress__container
        ${classNames}
      `}
    >
      {!isLive && (
        <>
          <div className="ino-player-progress__time-remaining">
            {duration && value !== undefined
              ? showLastTime
                ? formatTime(duration * (1 - value / 100))
                : formatTime((duration * value) / 100)
              : '00:00'}
          </div>

          {duration && value !== undefined ? (
            showTime ? (
              <div className="ino-player-progress__time_current">
                {formatTime((duration * value) / 100)}
              </div>
            ) : null
          ) : null}
          {showDuration ? (
            <div className="ino-player-progress__duration">
              {duration ? formatTime(duration) : '00:00'}
            </div>
          ) : null}
        </>
      )}
      <div
        ref={progressRef}
        className={`
        ino-player-progress 
        ${isActive ? 'ino-player-progress--active' : ''}
        ${isDragging ? 'ino-player-progress--dragging' : ''}
      `}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={(e) =>
          !isLive && !isDragging && handleMouseMove(e.nativeEvent)
        }
      >
        <div
          className="ino-player-progress__buffered"
          style={{ width: `${buffered}%` }}
        />
        <div
          className="ino-player-progress__value"
          style={{ width: `${isLive ? 100 : value}%` }}
        />
        {showTooltip && tooltipPosition >= 0 && (
          <div
            className="ino-player-progress__tooltip"
            style={{ left: `${tooltipPosition}%` }}
          >
            {formatTime(tooltipTime)}
          </div>
        )}
      </div>
    </div>
  );
};
